import { useEffect, useState, type ReactNode } from "react";
import { Download, FileText, RefreshCw } from "lucide-react";
import { ApiError, type AdminApi } from "@/api";
import { Badge, Banner, Button, Card } from "@/components/ui";
import type { LlmIoDetail, LlmIoEntry, LlmIoEnvironment, LlmIoStorage } from "@/types/llmIo";

export type LlmIoApi = Pick<AdminApi, "getLlmCallIo"> & Partial<Pick<AdminApi, "getSubAgentRoundIo">>;
/** A sub-agent round is read by its execution and round, not by a record id. */
export type SubAgentRound = { executionId: string; roundIndex: number };
export type LlmIoContext = { api: LlmIoApi; environment: LlmIoEnvironment };

export const callStatusLabel = (status: string) =>
  ({ pending: "저장 대기", complete: "완료", error: "오류" })[status] ?? status;

export function ioErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return "이 환경에서 입출력 조회 API 또는 해당 기록을 찾을 수 없습니다.";
  }
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return "입출력 원문을 조회할 관리자 권한이 없습니다.";
  }
  return `입출력 기록을 불러오지 못했습니다. ${error instanceof Error ? error.message : "잠시 후 다시 조회해 주세요."}`;
}

function downloadText(value: string, filename: string) {
  const url = URL.createObjectURL(new Blob([value], { type: "text/plain;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function OriginalBody({ title, value, filename, unrestorable = false }: {
  title: string; value: string | null; filename: string; unrestorable?: boolean;
}) {
  return (
    <section className="min-w-0" aria-label={title}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h4 className="text-sm font-medium">{title}</h4>
        {value !== null && (
          <Button variant="ghost" onClick={() => downloadText(value, filename)} className="text-xs">
            <Download />{title} 다운로드
          </Button>
        )}
      </div>
      {value === null ? (
        <p className="rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
          {unrestorable
            ? "기록은 있지만 원문을 복원할 수 없습니다. 참조한 프롬프트 버전이나 이전 라운드 기록이 삭제되었거나 바뀌었습니다."
            : "저장된 본문이 없습니다."}
        </p>
      ) : (
        <>
          {value.length === 0 && <p className="mb-1 text-xs text-muted-foreground">빈 문자열로 기록되었습니다.</p>}
          {/* No trim, clipping, JSON parsing or pretty-printing: this is the full string. */}
          <pre tabIndex={0} data-testid={title === "요청 원문" ? "io-request" : "io-response"}
            className="max-h-[60vh] min-h-16 max-w-full overflow-auto rounded-lg border border-border bg-surface-input p-3 font-mono text-xs whitespace-pre text-code-foreground"
          >{value}</pre>
        </>
      )}
    </section>
  );
}

function entryLabel(entry: LlmIoEntry, live: boolean) {
  if (live) {
    const direction = entry.direction === "send" ? "송신" : entry.direction === "receive" ? "수신" : entry.direction ?? "방향 미기록";
    return `메시지 ${entry.sequence ?? "순서 미기록"} · ${direction}`;
  }
  return entry.attempt_index === null ? "시도 번호 미기록" : `시도 ${entry.attempt_index + 1}`;
}

/** Explicit, optional read. Failure never participates in loading the existing trace. */
export function LlmIoPanel({ api, environment, recordId, storage = "turn", callId, round, onClose, onMissing }: LlmIoContext & {
  recordId?: string; storage?: LlmIoStorage; callId?: string | null; round?: SubAgentRound;
  onClose?: () => void;
  /** Shown instead of the error banner when there is no such record (404). */
  onMissing?: ReactNode;
}) {
  const [reload, setReload] = useState(0);
  const [selectedCapture, setSelectedCapture] = useState<string | null>(null);
  const executionId = round?.executionId;
  const roundIndex = round?.roundIndex;
  const key = JSON.stringify([environment, storage, recordId, callId, executionId, roundIndex, reload]);
  const [result, setResult] = useState<{ key: string; api: LlmIoApi; data?: LlmIoDetail; error?: string; missing?: boolean }>();
  useEffect(() => {
    let active = true;
    setSelectedCapture(null);
    const load = async () => {
      try {
        const data = executionId !== undefined && roundIndex !== undefined && api.getSubAgentRoundIo
          ? await api.getSubAgentRoundIo(executionId, roundIndex, { environment })
          : await api.getLlmCallIo(recordId ?? "", { environment, storage, callId });
        if (active) setResult({ key, api, data });
      } catch (error) {
        const missing = error instanceof ApiError && error.status === 404;
        if (active) setResult({ key, api, error: ioErrorMessage(error), missing });
      }
    };
    void load();
    return () => { active = false; };
  }, [api, environment, storage, recordId, callId, executionId, roundIndex, key]);
  const current = result?.key === key && result.api === api ? result : undefined;
  const data = current?.data;
  const entry = data?.entries.find((item) => item.capture_id === selectedCapture) ?? data?.entries[0];
  const live = data?.transport === "live";
  const liveContext = live && data?.scope === "live_session_context";

  return (
    <Card className="mt-3 grid min-w-0 gap-4 p-4" aria-label="LLM 입출력 원문">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="mr-auto flex items-center gap-2 font-semibold"><FileText className="size-4" />입출력 원문</h3>
        <Badge>{environment}</Badge>
        <Button variant="outline" onClick={() => setReload((value) => value + 1)}><RefreshCw />새로고침</Button>
        {onClose && <Button variant="ghost" onClick={onClose}>닫기</Button>}
      </div>
      <p className="text-xs text-muted-foreground">저장된 기록을 읽습니다. 새 모델 호출은 실행하지 않습니다. 음성 파일·음성 payload는 제외하며 제공된 STT·전사 텍스트를 표시합니다.</p>
      {!current && <p role="status" className="text-sm text-muted-foreground">입출력 기록 불러오는 중…</p>}
      {current?.error && (current.missing && onMissing
        ? onMissing
        : <Banner tone="error">{current.error} 기존 트레이스는 계속 볼 수 있습니다.</Banner>)}
      {data && (
        <>
          {liveContext && (
            <section className="grid gap-1.5 rounded-lg border border-border bg-surface-subtle p-3" aria-label="Live 조회 범위">
              <h4 className="text-sm font-semibold">이 턴이 속한 Live 세션 기록</h4>
              <p className="text-xs text-muted-foreground">턴 생성 전 기록과 같은 Live 세션의 다른 턴 기록도 포함됩니다. 선택한 턴만의 기록이 아닙니다.</p>
              {data.live_session_id && <p className="break-all font-mono text-xs text-muted-foreground">Live 세션: {data.live_session_id}</p>}
              {data.requested_turn_id && <p className="break-all font-mono text-xs text-muted-foreground">요청한 턴: {data.requested_turn_id}</p>}
            </section>
          )}
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <Badge>{data.storage === "raw" ? "기존 raw 기록" : "통합 기록"}</Badge>
            <Badge>{live ? `${liveContext ? "Live 세션" : "Live 구간"} · ${data.entries.length}개 메시지` : `${data.entries.length}개 시도`}</Badge>
            {data.session_id ? <span className="break-all">세션 {data.session_id}</span> : <span>세션 없는 호출</span>}
          </div>
          {data.entries.length === 0 && <p className="text-sm text-muted-foreground">저장된 입출력 기록이 없습니다.</p>}
          {data.entries.length > 0 && (
            <label className="grid gap-1.5 text-xs text-muted-foreground">
              {live ? "메시지 선택" : "호출·시도 선택"}
              <select value={entry?.capture_id ?? ""} onChange={(event) => setSelectedCapture(event.target.value)}
                className="min-w-0 w-full rounded-lg border border-border bg-surface-input px-3 py-2 text-foreground">
                {data.entries.map((item) => <option key={item.capture_id} value={item.capture_id}>
                  {entryLabel(item, live)}{live && item.connection_id ? ` · 연결 ${item.connection_id}` : ""} · {item.source || "출처 미기록"} · {callStatusLabel(item.status)}{item.status_code !== null ? ` · HTTP ${item.status_code}` : ""}{item.call_id ? ` · ${item.call_id}` : ""}
                </option>)}
              </select>
            </label>
          )}
          {entry && (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <strong>{entryLabel(entry, live)}</strong>
                <Badge>{callStatusLabel(entry.status)}</Badge>
                {entry.status_code !== null && <Badge>HTTP {entry.status_code}</Badge>}
                <span>{entry.source || "출처 미기록"}</span>
                <span className="text-muted-foreground">{entry.provider} / {entry.model ?? "모델 미기록"}</span>
                {entry.duration_ms !== null && <span className="text-muted-foreground">{entry.duration_ms} ms</span>}
              </div>
              {live && (liveContext || entry.connection_id) && <p aria-label="Live 연결" className="break-all font-mono text-xs text-muted-foreground">연결: {entry.connection_id ?? "미기록"}</p>}
              {entry.error && <Banner tone="error">{entry.error}</Banner>}
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">호출 정보</summary>
                <dl className="mt-2 grid gap-1 break-all">
                  <div>기록: {entry.capture_id}</div><div>호출: {entry.call_id ?? "미기록"}</div>
                  <div>시간: {entry.created_at ?? "미기록"}</div><div>주소: {entry.endpoint ?? "미기록"}</div>
                  {live && (liveContext || "owner_turn_id" in entry) && <div>소속 턴: {entry.owner_turn_id ?? "미귀속"}</div>}
                </dl>
              </details>
              {(!live || entry.direction !== "receive") && <OriginalBody title="요청 원문" value={entry.request_raw} filename={`${entry.capture_id}-request.txt`} unrestorable={entry.request_unrestorable === true} />}
              {(!live || entry.direction !== "send") && <OriginalBody title="응답 원문" value={entry.response_raw} filename={`${entry.capture_id}-response.txt`} />}
            </>
          )}
        </>
      )}
    </Card>
  );
}

/** Mount only after an explicit click; key this component by selected turn. */
export function TurnIoAction({ api, environment, turnId }: LlmIoContext & { turnId: string }) {
  const [open, setOpen] = useState(false);
  return open
    ? <LlmIoPanel api={api} environment={environment} recordId={turnId} onClose={() => setOpen(false)} />
    : <Button variant="outline" className="mt-3" onClick={() => setOpen(true)}><FileText />입출력 보기</Button>;
}

/** A sub-agent round's raw IO. Rounds recorded before each round got its own
 *  IO row have none; their calls sit in the parent turn's IO instead. */
export function SubAgentRoundIoAction({ api, environment, executionId, roundIndex, parentTurnId }: LlmIoContext & SubAgentRound & {
  parentTurnId: string | null;
}) {
  const [open, setOpen] = useState<"round" | "parent" | null>(null);
  if (open === "parent" && parentTurnId) {
    return <LlmIoPanel api={api} environment={environment} recordId={parentTurnId} onClose={() => setOpen(null)} />;
  }
  if (open === "round") {
    return (
      <LlmIoPanel api={api} environment={environment} round={{ executionId, roundIndex }} onClose={() => setOpen(null)}
        onMissing={(
          <div className="grid gap-2 rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
            <p>이 라운드만의 원문 기록이 없습니다. 라운드별 기록 도입 전에 저장된 실행은 서브에이전트 호출이 부모 턴 입출력에 출처 sub_agent로 함께 저장되어 있습니다.</p>
            {parentTurnId && (
              <Button variant="outline" className="justify-self-start" onClick={() => setOpen("parent")}>
                <FileText />부모 턴 입출력 보기
              </Button>
            )}
          </div>
        )} />
    );
  }
  return <Button variant="outline" className="mt-3" onClick={() => setOpen("round")}><FileText />입출력 보기</Button>;
}
